import { Table, TableBody, TableCell, TableHead, TableRow } from "../../../common/components/Table/TableElements";
import dayjs from "dayjs";
import { NumberFormatter } from "~/common/utils/NumberFormatter";

import { DateFormatType } from "~/common/utils/DateFormatter";
import { Trans, useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, Card } from "~/common/mui";
import { getPhysicalDeliveries, isReplenishment } from "~/utils/cartUpdating/delivery/stockDataUtils";
import { type StockData } from "~/utils/cartUpdating/delivery/stockDataTypes";
import { Alert } from "~/common/mui/Alert/Alert";
import { formatShippingOrDeliveryDate } from "~/common/utils/dateUtils";
import { isShipmentDateMeaning } from "~/app/config/deliveryDateMeaning";
import { useSession } from "~/app/providers/SessionProvider";

interface PartialDeliveriesSectionProps {
  partialDeliveries?: StockData;
}

export const PartialDeliveriesSection = ({ partialDeliveries }: PartialDeliveriesSectionProps) => {
  const { t } = useTranslation();
  const deliveryDateMeaning = useSession()?.commerceContext.deliveryDateMeaning;

  if (!isPartialDeliveriesVisible(partialDeliveries)) {
    return null;
  }

  const replenishmentDelivery = partialDeliveries.find(isReplenishment);
  const tableDeliveries = getPhysicalDeliveries(partialDeliveries);

  if (tableDeliveries.length <= 1) {
    if (replenishmentDelivery) {
      return (
        <Alert severity={"info"}>
          {t("partialDeliveries.replenishment.full", getNumberProps(replenishmentDelivery.amount))}
        </Alert>
      );
    }
    return null;
  }

  const today = dayjs().startOf("day");
  const getDays = (date: Date) => dayjs(date).startOf("day").diff(today, "day");

  const table = (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <strong>{t("wishlistPage.table.header.number")}</strong>
          </TableCell>
          <TableCell>
            <strong>{t("input.quantity")}</strong>
          </TableCell>
          <TableCell>
            <strong>{t("orderList.date")}</strong>
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {tableDeliveries.map((entry, index) => {
          const count = getDays(entry.date!);
          return (
            <TableRow>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{NumberFormatter.getFormatter().format(entry.amount)}</TableCell>
              <TableCell>{formatShippingOrDeliveryDate(entry.date!, DateFormatType.DATE, "")}</TableCell>
              <TableCell>
                <Trans
                  i18nKey={`partialDeliveries.${isShipmentDateMeaning(deliveryDateMeaning) ? "shipment" : "arrival"}.${
                    count === 0 ? "today" : "default"
                  }`}
                  count={count}
                >
                  arrival expected in <b>{{ count }} day</b>
                </Trans>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <Accordion component={Card} sx={{ "&::before": { display: "none" }, "&::after": { display: "none" } }}>
      <AccordionSummary>{t("partialDeliveries.title", { count: tableDeliveries.length })}</AccordionSummary>
      <AccordionDetails>
        {table}
        {replenishmentDelivery && (
          <Alert severity={"info"} className={"mt-2"}>
            {t("partialDeliveries.replenishment.partial", getNumberProps(replenishmentDelivery.amount))}
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const getNumberProps = (count: number) => ({ count, amount: NumberFormatter.getDefaultFormatter().format(count) });

export function isPartialDeliveriesVisible(partialDeliveries?: StockData): partialDeliveries is StockData {
  return !!(partialDeliveries && partialDeliveries.length);
}
